import { useState, useEffect, useCallback } from 'react';
import { getPrompt, updatePrompt, deletePrompt, testPrompt, getPromptAnalytics } from '../services/aiOperationsApi';

/**
 * Custom hook for managing a single prompt
 *
 * @param promptId - Prompt ID to manage
 * @returns Prompt state and operations
 */
export const usePrompt = (promptId: string | null | undefined) => {
  const [prompt, setPrompt] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [testing, setTesting] = useState<boolean>(false);
  const [testResult, setTestResult] = useState<Record<string, any> | null>(null);

  // Fetch prompt data
  const fetchPrompt = useCallback(async (): Promise<void> => {
    if (!promptId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await (getPrompt as (name: string, version?: number) => Promise<any>)(promptId);
      setPrompt(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching prompt:', err);
    } finally {
      setLoading(false);
    }
  }, [promptId]);

  // Load prompt on mount or when ID changes
  useEffect(() => {
    fetchPrompt();
  }, [fetchPrompt]);

  // Update prompt
  const update = useCallback(async (updates: Record<string, any>): Promise<any> => {
    if (!promptId) return;

    setSaving(true);
    setError(null);

    try {
      const result = await updatePrompt(promptId, updates);
      setPrompt(result.prompt);
      return result;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error updating prompt:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [promptId]);

  // Delete prompt
  const remove = useCallback(async (): Promise<void> => {
    if (!promptId) return;

    try {
      await deletePrompt(promptId);
      setPrompt(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error deleting prompt:', err);
      throw err;
    }
  }, [promptId]);

  // Test prompt with variables
  const test = useCallback(async (variables: Record<string, any>, modelConfig: Record<string, any> | undefined = undefined): Promise<any> => {
    if (!promptId) return;

    setTesting(true);
    setError(null);

    try {
      const result = await testPrompt(promptId, variables, modelConfig as any);
      setTestResult(result);
      return result;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error testing prompt:', err);
      throw err;
    } finally {
      setTesting(false);
    }
  }, [promptId]);

  // Toggle active status
  const toggleActive = useCallback(async (): Promise<any> => {
    if (!prompt) return;

    return update({ is_active: !prompt.is_active });
  }, [prompt, update]);

  // Refresh prompt data
  const refresh = fetchPrompt;

  return {
    prompt,
    loading,
    error,
    saving,
    testing,
    testResult,
    update,
    remove,
    test,
    toggleActive,
    refresh
  };
};

/**
 * Custom hook for prompt analytics
 *
 * @param promptId - Prompt ID
 * @param days - Time range in days
 * @returns Analytics state
 */
export const usePromptAnalytics = (promptId: string | null | undefined, days: number = 30) => {
  const [analytics, setAnalytics] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async (): Promise<void> => {
    if (!promptId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getPromptAnalytics(promptId, days);
      setAnalytics(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [promptId, days]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return {
    analytics,
    loading,
    error,
    refresh: fetchAnalytics
  };
};

export default usePrompt;
